// Copilot: Legal page component for terms, privacy, cookies, and accessibility content
// Displays appropriate legal content based on page prop with back navigation

import React from 'react';
import Footer from './Footer';

interface LegalPageProps {
  page: 'terms' | 'privacy' | 'cookies' | 'accessibility';
  onNavigate: (page: string) => void;
}

const LegalPage: React.FC<LegalPageProps> = ({ page, onNavigate }) => {
  const getContent = () => {
    switch (page) {
      case 'terms':
        return {
          title: 'Terms of Service',
          content: [
            'Welcome to its my merge. By using our service, you agree to these terms.',
            'You may use our PDF merging service for lawful purposes only. You retain ownership of all files you upload.',
            'We reserve the right to modify these terms at any time. Continued use constitutes acceptance of changes.',
            'Our service is provided "as is" without warranties. We are not liable for any damages arising from use.',
            'These terms are governed by the laws of the United Kingdom.'
          ]
        };
      case 'privacy':
        return {
          title: 'Privacy Policy',
          content: [
            'Your privacy is important to us. This policy explains how we collect and use your information.',
            'We collect files you upload for processing, account information if you register, and usage analytics.',
            'Files are processed securely and deleted after a reasonable time. We do not share your content with third parties.',
            'We use Firebase for authentication and storage, which has its own privacy practices.',
            'You can request deletion of your data by contacting hello@itsmyapp.co.uk.'
          ]
        };
      case 'cookies':
        return {
          title: 'Cookie Policy',
          content: [
            'We use cookies and similar technologies to provide and improve our service.',
            'Essential cookies are required for authentication and core functionality.',
            'Analytics cookies help us understand how users interact with our service.',
            'You can control cookie preferences in your browser settings.',
            'Disabling certain cookies may affect service functionality.'
          ]
        };
      case 'accessibility':
        return {
          title: 'Accessibility Statement',
          content: [
            'We are committed to making our service accessible to all users.',
            'Our interface follows WCAG 2.1 guidelines for keyboard navigation and screen readers.',
            'File upload supports drag-and-drop as well as traditional file selection.',
            'We provide alternative text for images and clear form labels.',
            'If you encounter accessibility issues, please contact hello@itsmyapp.co.uk.'
          ]
        };
      default:
        return { title: 'Legal Information', content: ['Legal content not found.'] };
    }
  };

  const { title, content } = getContent();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src="/its_my_merge.png"
                alt="its my merge"
                className="h-10 w-auto"
              />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-orange-600 bg-clip-text text-transparent">
                its my merge
              </h1>
            </div>
            <button
              onClick={() => onNavigate('app')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
            >
              ← Back to App
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-8 border border-gray-100 dark:border-gray-700">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{title}</h2>
            <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-orange-500 rounded-full"></div>
          </div>

          <div className="prose max-w-none">
            {content.map((paragraph, index) => (
              <p key={index} className="mb-6 text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Last updated: November 2, 2025
            </p>
          </div>
        </div>
      </div>

      <Footer onNavigate={onNavigate} />
    </div >
  );
};

export default LegalPage;